import type { FeatureLike } from 'ol/Feature';
import { Fill, Stroke, Style, Text } from 'ol/style';
import type { CasurHydricClass, CasurHydricKind, CasurHydricStatus } from '../core/types';

const maxWebMercatorResolution = 156543.03392804097;
const widths: Record<CasurHydricClass, number> = { principal: 3.2, secondary: 2.25, tertiary: 1.45, unclassified: 1.1 };
const channelColors: Record<CasurHydricClass, string> = { principal: '#00c8ff', secondary: '#55dcff', tertiary: '#9deaff', unclassified: '#b8cbd1' };
const drainageColors: Record<CasurHydricClass, string> = { principal: '#275fd6', secondary: '#4c84e5', tertiary: '#88acec', unclassified: '#9aa9ba' };
const cache = new Map<string, Style[]>();

export const casurHydricStyle = (kind: CasurHydricKind, selectedId: string | null) => (feature: FeatureLike, resolution: number): Style[] => {
  const featureId = String(feature.get('casurHydricId') ?? '');
  const selected = featureId === selectedId;
  const hydricClass = (feature.get('casurHydricClass') ?? 'unclassified') as CasurHydricClass;
  const status = (feature.get('casurHydricStatus') ?? 'unknown') as CasurHydricStatus;
  const zoom = Math.log2(maxWebMercatorResolution / resolution);
  const label = String(feature.get('casurHydricLabel') ?? '');
  const showLabel = Boolean(label) && zoom >= (kind === 'channels' ? 15 : 16);
  const key = `${kind}:${hydricClass}:${status}:${selected}:${showLabel ? label : ''}`;
  const existing = cache.get(key); if (existing) return existing;
  const color = kind === 'channels' ? channelColors[hydricClass] : drainageColors[hydricClass];
  const width = widths[hydricClass];
  const styles = selected
    ? [new Style({ stroke: new Stroke({ color: 'rgba(255,176,0,.95)', width: width + 5 }), zIndex: 80 })]
    : [];
  styles.push(new Style({
    stroke: new Stroke({ color, width: selected ? width + 1 : width, lineDash: kind === 'channels' && status === 'projected' ? [9, 6] : undefined }),
    text: showLabel ? new Text({ text: label, placement: 'line', overflow: false, font: '600 10px Inter, system-ui, sans-serif', fill: new Fill({ color: '#f4fbff' }), stroke: new Stroke({ color: 'rgba(9,32,44,.95)', width: 3 }) }) : undefined,
    zIndex: hydricClass === 'principal' ? 50 : hydricClass === 'secondary' ? 40 : 30,
  }));
  cache.set(key, styles); return styles;
};
